import ClickAwayListener from "@mui/material/ClickAwayListener";
import classNames from "classnames";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { clickOnSpaceOrEnterPress } from "utils/keyboardUtils";

import { MyAccount } from "services/account/account.types";
import { useAccounts } from "services/account/useAccounts";
import { authService } from "services/auth";
import { useHasAccountAdminRole } from "services/auth/useHasAccountAdminRole";
import { useUser } from "services/auth/useUser";
import { Login } from "services/login/login.types";
import { useLogin } from "services/login/useLogin";

import { useCurrentAccount } from "components/Account/useCurrentAccount";
import ActionMenu from "components/ActionMenu/ActionMenu";
import { useMenu } from "components/ActionMenu/useMenu";
import ContextMenu from "components/ContextMenu/ContextMenu";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import NestedContextMenuItem from "components/ContextMenu/NestedContextMenuItem";
import { ChevronRight } from "components/Icons";
import Tooltip from "components/Tooltip";

import { AQA_AVATAR_NAME, AQA_LOGOUT_LINK } from "../AqaIds";
import { useResetPassword } from "./useResetPassword";

import sidebarStyles from "../MainSidebar.module.scss";
import styles from "../sidebarItemWithDropdown.module.scss";

const getAvatarAbbr = (name: string) => {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .map(c => c[0])
    .slice(0, 2)
    .join("");
};

type Props = {
  accounts: MyAccount[];
  currentAccount: MyAccount | undefined;
  login: Login | undefined;
  roles?: { roleName: string }[];
};

const UserBar = (props: Props) => {
  const { accounts, currentAccount, login, roles = [] } = props;
  const user = useUser();
  const { t } = useTranslation();
  const email = user?.email ?? "";
  const name = login?.loginName || email;

  const { menuElement, openMenu, closeMenu } = useMenu();
  const { handleResetPassword, isLoading: resetPasswordLoading } =
    useResetPassword({
      closeMenu,
      loginName: login ? login.loginName : "",
    });

  const handleLogout = useCallback(async () => {
    await authService.redirectToLogout();
    closeMenu();
  }, [closeMenu]);

  const visibleRolesList = useMemo(() => {
    const list = [];
    for (const role of roles || []) {
      const { roleName } = role;
      const totalLength =
        list.reduce((sum, r) => sum + r.length, 0) + roleName.length;
      if (totalLength < 30) {
        list.push(roleName);
      } else {
        return list;
      }
    }
    return list;
  }, [roles]);

  const rolesLeft = roles.length - visibleRolesList.length;

  const header = accounts.length
    ? [
        <div
          className={styles.header}
          key="name"
        >
          <div
            className={styles.loginName}
            title=""
          >
            {name}
          </div>
          <div className={styles.roles}>
            <span>{visibleRolesList.join(", ")}</span>

            {rolesLeft ? (
              <Tooltip
                className={styles.roles__tooltip}
                title={
                  <div className={styles.roles__list}>
                    <div className={styles.roles__header}>
                      {t("userbar.roles")}
                    </div>
                    {roles.map(r => (
                      <div
                        key={r.roleName}
                        className={styles.roles__item}
                      >
                        {r.roleName}
                      </div>
                    ))}
                  </div>
                }
                placement="right-end"
              >
                <span className={styles.roles__more}>
                  {t("userbar.more", { rolesLeft })}
                </span>
              </Tooltip>
            ) : null}
          </div>
        </div>,
      ]
    : [];

  const mainItems = accounts.length
    ? [
        [
          accounts.map(account => {
            const isActive = account.id === currentAccount?.id;
            const text = (
              <div
                className={classNames(styles.accountItem, {
                  [styles.accountItem__active]: isActive,
                })}
              >
                <div
                  className={styles.accountItem__name}
                  title={account.accountName}
                >
                  {account.accountName}
                </div>
              </div>
            );
            return isActive ? (
              <ContextMenuItem
                key={account.id}
                text={text}
                secondaryText={account.region}
              />
            ) : (
              <Link
                to={`/${account.accountName}`}
                key={account.id}
              >
                <ContextMenuItem
                  text={text}
                  secondaryText={account.region}
                />
              </Link>
            );
          }),
        ],
      ]
    : [];

  const secondaryItems = [
    ...(login
      ? [
          <ContextMenuItem
            text={
              resetPasswordLoading ? t("userbar.resetting") : t("userbar.reset")
            }
            testId="reset-password"
            key="reset-password"
            onClick={event => {
              event.stopPropagation();
              handleResetPassword();
            }}
            disabled={resetPasswordLoading}
          />,
        ]
      : []),
    <ContextMenuItem
      text={t("userbar.signout")}
      testId={AQA_LOGOUT_LINK}
      key="log_out"
      onClick={handleLogout}
    />,
  ];

  const renderContent = () => {
    return <ContextMenu>{mainItems}</ContextMenu>;
  };

  const menuItems = [
    header,
    ...(accounts.length
      ? [
          [
            <NestedContextMenuItem
              key="account"
              renderText={({ textItemClassName }) => (
                <div className={classNames(styles.account, textItemClassName)}>
                  <div className={styles.accountItem__title}>
                    {t("userbar.account")}
                  </div>
                  <div
                    className={classNames(
                      styles.accountItem,
                      styles.accountItem__active
                    )}
                  >
                    <div className={styles.accountItem__name}>
                      {currentAccount?.accountName}
                    </div>{" "}
                    <div className={styles.accountItem__region}>
                      {currentAccount?.region}
                      <div className={styles.checkedWrapper}>
                        <ChevronRight />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              itemClassName={styles.account__item}
              renderContent={renderContent}
              contentPaperStyle={{ width: 300 }}
            />,
          ],
        ]
      : []),
    secondaryItems,
  ];

  return (
    <ClickAwayListener onClickAway={() => closeMenu()}>
      <div>
        <Tooltip
          title={name}
          placement="right"
          classes={{
            popper: classNames(
              sidebarStyles.menuItemTooltip,
              sidebarStyles.forced
            ),
          }}
        >
          <div
            className={styles.root}
            onClick={openMenu}
            tabIndex={0}
            onKeyUp={event => clickOnSpaceOrEnterPress(event)}
          >
            <div className={styles.container}>
              <span
                className={styles.name}
                data-testid={AQA_AVATAR_NAME}
              >
                {getAvatarAbbr(name)}
              </span>
            </div>
          </div>
        </Tooltip>
        {!!menuElement && (
          <ActionMenu
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            lists={menuItems.filter(list => list.length)}
            open={true}
            anchorEl={menuElement}
            onClose={closeMenu}
          />
        )}
      </div>
    </ClickAwayListener>
  );
};

export const UserBarWithoutAccounts = () => {
  return (
    <UserBar
      accounts={[]}
      currentAccount={undefined}
      login={undefined}
    />
  );
};

export const UserBarWithAccounts = () => {
  const { getAccount } = useCurrentAccount();
  const currentAccount = getAccount();
  const accounts = useAccounts();
  const login = useLogin();
  const { roles } = useHasAccountAdminRole();

  return (
    <UserBar
      accounts={accounts}
      currentAccount={currentAccount}
      login={login}
      roles={roles}
    />
  );
};
